import { useIsMutating } from "@tanstack/react-query"
import { Save, Trash2 } from "lucide-react"
import { useStore } from "zustand"

import { Button } from "@/components/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  DELETE_TALENT_MUTATION_KEY,
  useUpdateTalentMutation,
  type TalentDraft,
} from "@/hooks/talent-config"
import {
  useTalentConfigRouteStore,
  updateTalentDraftField,
  setDeleteTarget,
} from "@/lib/stores/talent-config-ui-store"
import { type TalentConfigTalent } from "@/lib/talent-config-api"
import {
  isTalentDirty,
  validateTalentDraft,
} from "@/lib/utils/talent-draft.utils"

export function TierRow({ talent }: { talent: TalentConfigTalent }) {
  // This component only subscribes to its own talent draft!
  const store = useTalentConfigRouteStore()

  const talentDraft = useStore(store, (s) => s.talentDrafts[talent.id])

  const updateTalentMutation = useUpdateTalentMutation(store)

  const isDeletingTalent =
    useIsMutating({ mutationKey: DELETE_TALENT_MUTATION_KEY }) > 0

  // If draft doesn't exist, initialize with current talent values
  const draft = talentDraft ?? {
    description: talent.description,
    reward: talent.reward,
  }

  const dirty = isTalentDirty(draft, talent)
  const draftValidation = validateTalentDraft(draft)

  return (
    <div className="grid gap-3 rounded-[1.35rem] border border-border/60 bg-card/40 p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-kicker text-xs text-muted-foreground">
            Tier {talent.tier}
          </div>
          <div className="mt-1 text-sm font-semibold text-foreground">
            {talent.id}
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          disabled={
            updateTalentMutation.isPending ||
            !dirty ||
            Boolean(draftValidation.description || draftValidation.reward)
          }
          onClick={() => {
            updateTalentMutation.mutate({
              talent,
              draft,
            })
          }}
          type="button"
        >
          <Save className="size-3.5" />
          Save
        </Button>
        <Button
          size="sm"
          variant="destructive"
          disabled={isDeletingTalent}
          onClick={() => {
            setDeleteTarget(store, {
              kind: "talent",
              id: talent.id,
              category: talent.category,
              tier: talent.tier,
            })
          }}
          type="button"
        >
          <Trash2 className="size-3.5" />
          Delete
        </Button>
      </div>

      <div className="grid gap-2">
        <Label htmlFor={`${talent.id}-description`}>Requirement</Label>
        <Textarea
          id={`${talent.id}-description`}
          value={draft.description}
          onChange={(event) =>
            updateTalentDraftField(
              store,
              talent.id,
              "description",
              event.target.value,
            )
          }
        />
        {draftValidation.description ? (
          <p className="text-xs text-destructive">
            {draftValidation.description}
          </p>
        ) : null}
      </div>

      <div className="grid gap-2">
        <Label htmlFor={`${talent.id}-reward`}>Reward</Label>
        <Input
          id={`${talent.id}-reward`}
          value={draft.reward}
          onChange={(event) =>
            updateTalentDraftField(
              store,
              talent.id,
              "reward",
              event.target.value,
            )
          }
        />
        {draftValidation.reward ? (
          <p className="text-xs text-destructive">{draftValidation.reward}</p>
        ) : null}
      </div>
    </div>
  )
}
